import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, Input, Message, Popconfirm, Table, Typography } from "@arco-design/web-react";
import { IconCopy, IconDelete } from "@arco-design/web-react/icon";
import moment from "moment";

import { FetchApi, PostApi, DeleteApi } from "../../Api/AuthenticatedApi";

const { Text } = Typography;

// API keys let another app (like recon) call this account's /api/connect
// routes without logging in. The key itself is shown once, right after it is
// made; after that only its first few characters are shown.
export default function ApiKeys() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [keys, setKeys] = useState([]);
  const [refresh, setRefresh] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  // The key just made: { name, secret }. Cleared when dismissed.
  const [made, setMade] = useState(null);

  useEffect(() => {
    if (!refresh) return;
    FetchApi(getAccessTokenSilently, "/api/settings/api-keys/",
      (data) => { setKeys(Array.isArray(data) ? data : []); setRefresh(false); },
      () => navigate("/login"),
      () => { setRefresh(false); Message.error("Failed to load API keys"); });
  }, [getAccessTokenSilently, navigate, refresh]);

  const create = () => {
    if (!name.trim()) { Message.warning("Give the key a name"); return; }
    setCreating(true);
    PostApi(getAccessTokenSilently, "/api/settings/api-keys/",
      (data) => {
        setCreating(false);
        setName("");
        setMade({ name: data.key && data.key.name, secret: data.secret });
        setRefresh(true);
      },
      () => navigate("/login"),
      (err) => { setCreating(false); Message.error((err && err.message) || "Failed to create API key"); },
      { name: name.trim() });
  };

  const revoke = (key) => {
    DeleteApi(getAccessTokenSilently, "/api/settings/api-keys/" + key.id,
      () => { Message.success("API key revoked"); setRefresh(true); },
      () => navigate("/login"),
      () => Message.error("Failed to revoke API key"),
      {});
  };

  const copy = (value) => {
    // navigator.clipboard needs a secure context; the textarea fallback keeps
    // copying working over plain http in dev.
    const done = () => Message.success("Key copied");
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(done, () => Message.error("Couldn't copy"));
      return;
    }
    const field = document.createElement("textarea");
    field.value = value;
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    try { document.execCommand("copy"); done(); } catch (e) { Message.error("Couldn't copy"); }
    field.remove();
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Key", dataIndex: "prefix", render: (p) => <Text code>{p}…</Text> },
    { title: "Created", dataIndex: "created_date", render: (d) => (d ? moment(d).format("MMM D, YYYY") : "") },
    { title: "Last used", dataIndex: "last_used_date", render: (d) => (d ? moment(d).fromNow() : "Never") },
    {
      title: "",
      render: (_, k) => (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Popconfirm
            title={`Revoke ${k.name}?`}
            content="Apps using this key stop working right away. This can't be undone."
            okText="Revoke key"
            onOk={() => revoke(k)}
          >
            <Button shape="circle" type="text" icon={<IconDelete />} aria-label={`Revoke ${k.name}`} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Text type="secondary">
        An API key lets another app use this account without logging in, for example to set up
        Email Spy watches. Send it in the <Text code>Authorization: Bearer</Text> header. Anyone
        with the key can do this, so keep it secret.
      </Text>

      {made && (
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          title={`Your new key${made.name ? " \"" + made.name + "\"" : ""}`}
          closable
          onClose={() => setMade(null)}
          content={
            <div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "4px 0" }}>
                <Text code style={{ wordBreak: "break-all" }}>{made.secret}</Text>
                <Button size="small" icon={<IconCopy />} onClick={() => copy(made.secret)}>Copy</Button>
              </div>
              Copy it now. It won't be shown again. If you lose it, revoke it and make a new one.
            </div>
          }
        />
      )}

      <Table
        style={{ marginTop: 16 }}
        size="small"
        columns={columns}
        data={keys}
        rowKey="id"
        pagination={false}
        border={false}
        noDataElement={<span style={{ color: "var(--color-text-3)" }}>No API keys yet.</span>}
      />

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        <Input style={{ width: 280 }} placeholder="Key name, like Recon" value={name} onChange={setName} onPressEnter={create} />
        <Button type="primary" loading={creating} onClick={create}>Create key</Button>
      </div>
    </div>
  );
}
